/**
 * Stimulus mixin for managing template instantiation and content population.
 * Provides a generic way to clone templates and populate them with data.
 * 
 * Usage:
 * import { useTemplateManager } from "../mixins/useTemplateManager";
 * 
 * export default class extends Controller {
 *   static targets = ["myTemplate", "container"];
 * 
 *   connect() {
 *     useTemplateManager(this);
 *   }
 * 
 *   // Basic instantiation
 *   someAction() {
 *     const element = this.instantiateTemplate({
 *       template: this.myTemplateTarget,
 *       content: {
 *         '[data-message-placeholder]': { textContent: 'Hello World' },
 *         '[data-progress-bar]': { style: { width: '50%' } },
 *         '[data-list-container]': { innerHTML: '<li>Item 1</li><li>Item 2</li>' },
 *         '.class-selector': { classList: ['active', 'highlight'] },
 *         '#id-selector': { attributes: { 'aria-expanded': 'true' } }
 *       },
 *       rootElement: { dataset: { id: 'example' } }
 *     });
 *     this.containerTarget.appendChild(element);
 *   }
 * 
 *   // Create and append in one step
 *   anotherAction() {
 *     this.createAndAppendTemplate({
 *       template: this.myTemplateTarget,
 *       container: this.containerTarget, // or 'container' for target name
 *       content: {
 *         '[data-title]': { textContent: 'New Title' },
 *         '[data-status]': { textContent: 'Active', classList: ['status-active'] }
 *       },
 *       replaceContent: true // Clear container first
 *     });
 *   }
 * }
 */
export const useTemplateManager = (controller) => {
  Object.assign(controller, {
    /**
     * Instantiate a template and populate it with content
     * @param {Object} options - Options object
     * @param {Element} options.template - Template element to clone
     * @param {Object} options.content - Map of selectors to content/properties
     * @param {Object} options.rootElement - Properties to apply to the root element
     * @returns {Element} - The cloned and populated template element
     */
    instantiateTemplate({ template, content = {}, rootElement = {} }) {
      if (!template) {
        console.error('Template element is required');
        return null;
      }

      // Clone the template
      const clonedElement = template.content.cloneNode(true).firstElementChild;
      if (!clonedElement) {
        console.error('No content found in template');
        return null;
      }

      // Set any direct properties on the root element
      if (Object.keys(rootElement).length > 0) {
        this._applyPropertiesToElement(clonedElement, rootElement);
      }

      // Populate content using the content map
      Object.entries(content).forEach(([selector, config]) => {
        this._populateTemplateElement(clonedElement, selector, config);
      });

      return clonedElement;
    },

    /**
     * Populate a single element within a template based on selector
     * @param {Element} container - The container element to search within
     * @param {string} selector - The CSS selector to find the element
     * @param {Object} config - Configuration for how to populate the element
     */
    _populateTemplateElement(container, selector, config) {
      const element = container.querySelector(selector);
      if (!element) {
        console.warn(`Element with selector '${selector}' not found in template`);
        return;
      }

      // Apply configuration to element
      this._applyPropertiesToElement(element, config);
    },

    /**
     * Apply properties to an element
     * @param {Element} element - The element to modify
     * @param {Object} config - Configuration object with properties to set
     */
    _applyPropertiesToElement(element, config) {
      Object.entries(config).forEach(([property, value]) => {
        if (property === 'style' && typeof value === 'object') {
          Object.entries(value).forEach(([styleProp, styleValue]) => {
            element.style[styleProp] = styleValue;
          });
        } else if (property === 'dataset' && typeof value === 'object') {
          Object.entries(value).forEach(([dataProp, dataValue]) => {
            element.dataset[dataProp] = dataValue;
          });
        } else if (property === 'classList' && Array.isArray(value)) {
          value.forEach(className => element.classList.add(className));
        } else if (property === 'attributes' && typeof value === 'object') {
          Object.entries(value).forEach(([attrName, attrValue]) => {
            element.setAttribute(attrName, attrValue);
          });
        } else {
          element[property] = value;
        }
      });
    },

    /**
     * Create and populate a template element, then append it to a container
     * @param {Object} options - Options object
     * @param {Element} options.template - Template element to clone
     * @param {Element|string} options.container - Container element or target name
     * @param {Object} options.content - Content map for population
     * @param {Object} options.rootElement - Properties to apply to the root element
     * @param {boolean} options.replaceContent - Whether to clear container before appending
     * @returns {Element} - The created element
     */
    createAndAppendTemplate({ template, container, content = {}, rootElement = {}, replaceContent = false }) {
      const element = this.instantiateTemplate({ template, content, rootElement });
      if (!element) return null;

      // Resolve container if it's a string (target name)
      const containerElement = typeof container === 'string' 
        ? this[`${container}Target`] 
        : container;

      if (containerElement) {
        if (replaceContent) {
          containerElement.innerHTML = '';
        }
        containerElement.appendChild(element);
      }

      return element;
    },

    /**
     * Convenience method for updating existing template content
     * @param {Element} container - Container element to search within
     * @param {Object} content - Content map for population using selectors
     */
    updateTemplateContent(container, content = {}) {
      Object.entries(content).forEach(([selector, config]) => {
        this._populateTemplateElement(container, selector, config);
      });
    }
  });
}; 